import { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '@/App';
import { ShoppingCart, Trash2, CreditCard, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cfc143ec-270d-4208-9592-8eae45ed356e/artifacts/4ltgv78b_Untitled%20design-2.png';

const CheckoutPage = () => {
  const { cart, cartTotal, removeFromCart, updateQuantity, clearCart } = useContext(CartContext);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    payment_method: 'dummy_card'
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.customer_address) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);

    // Simulate order placement (no backend needed for demo)
    setTimeout(() => {
      toast.success('Order placed successfully! (Demo mode - no actual order created)');
      clearCart();
      setLoading(false);
      setTimeout(() => navigate('/'), 2000);
    }, 1000);
  };

  return (
    <div className="app-container" data-testid="checkout-page">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="logo-container" data-testid="home-link">
            <img src={LOGO_URL} alt="Om Jishnu Logo" className="logo" />
            <span className="brand-name">OM JISHNU</span>
          </Link>
          <div className="nav-links">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/products" className="nav-link" data-testid="nav-products">Products</Link>
            <Link to="/admin" className="nav-link" data-testid="nav-admin">Admin</Link>
            <Link to="/checkout" className="cart-badge" data-testid="nav-cart">
              <ShoppingCart size={24} />
              {cart.length > 0 && (
                <span className="cart-count" data-testid="cart-count">{cart.length}</span>
              )}
            </Link>
          </div>
        </div>
      </nav>

      {/* Page Header */}
      <div style={{ background: 'linear-gradient(135deg, #F4C430 0%, #A44A66 100%)', padding: '3rem 2rem', color: 'white' }} data-testid="page-header">
        <div className="container">
          <h1 style={{ fontSize: '3rem', fontWeight: '700' }} data-testid="page-title">Checkout</h1>
        </div>
      </div>

      {/* Checkout Content */}
      <div className="container" style={{ padding: '4rem 2rem' }}>
        {cart.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }} data-testid="empty-cart">
            <ShoppingCart size={80} style={{ color: '#ddd', margin: '0 auto 2rem' }} />
            <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Your cart is empty</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>Add some products to get started</p>
            <Button className="btn-primary" onClick={() => navigate('/products')} data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '3rem' }}>
            {/* Cart Items */}
            <div>
              <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', fontWeight: '700' }} data-testid="cart-items-title">Cart Items</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {cart.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      background: 'white',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      display: 'flex',
                      gap: '1rem'
                    }}
                    data-testid={`cart-item-${item.id}`}
                  >
                    <img
                      src={item.image_url}
                      alt={item.name}
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.25rem' }} data-testid="cart-item-name">{item.name}</h3>
                      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }} data-testid="cart-item-brand">{item.brand}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            style={{ padding: '0.25rem 0.5rem', background: 'white', border: 'none', cursor: 'pointer' }}
                            data-testid="decrease-quantity"
                          >
                            -
                          </button>
                          <span style={{ padding: '0.25rem 0.75rem', fontWeight: '600' }} data-testid="cart-item-quantity">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            style={{ padding: '0.25rem 0.5rem', background: 'white', border: 'none', cursor: 'pointer' }}
                            data-testid="increase-quantity"
                          >
                            +
                          </button>
                        </div>
                        <div style={{ fontWeight: '700', color: '#A44A66', fontSize: '1.1rem' }} data-testid="cart-item-price">
                          ₹{(item.price * item.quantity).toLocaleString()}
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                          data-testid="remove-item"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: '#f8f8f8',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                data-testid="cart-total"
              >
                <span style={{ fontSize: '1.5rem', fontWeight: '700' }}>Total:</span>
                <span style={{ fontSize: '2rem', fontWeight: '700', color: '#A44A66' }} data-testid="total-amount">₹{cartTotal.toLocaleString()}</span>
              </div>
            </div>

            {/* Checkout Form */}
            <div>
              <h2 style={{ fontSize: '1.8rem', marginBottom: '1.5rem', fontWeight: '700' }} data-testid="checkout-form-title">Customer Details</h2>
              <form onSubmit={handleSubmit} style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                <div className="form-group">
                  <Label htmlFor="customer_name" data-testid="name-label">Full Name *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="customer_email" data-testid="email-label">Email *</Label>
                  <Input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-email-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="customer_phone" data-testid="phone-label">Phone *</Label>
                  <Input
                    id="customer_phone"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-phone-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="customer_address" data-testid="address-label">Address *</Label>
                  <Input
                    id="customer_address"
                    name="customer_address"
                    value={formData.customer_address}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-address-input"
                  />
                </div>
                <div className="form-group">
                  <Label data-testid="payment-label">Payment Method</Label>
                  <RadioGroup value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', marginBottom: '0.5rem' }}>
                      <RadioGroupItem value="dummy_card" id="card" data-testid="payment-card" />
                      <Label htmlFor="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                        <CreditCard size={20} /> Dummy Card (Test)
                      </Label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', marginBottom: '0.5rem' }}>
                      <RadioGroupItem value="dummy_upi" id="upi" data-testid="payment-upi" />
                      <Label htmlFor="upi" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                        <Smartphone size={20} /> Dummy UPI (Test)
                      </Label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px' }}>
                      <RadioGroupItem value="cod" id="cod" data-testid="payment-cod" />
                      <Label htmlFor="cod" style={{ cursor: 'pointer', margin: 0 }}>Cash on Delivery</Label>
                    </div>
                  </RadioGroup>
                </div>
                <Button
                  type="submit"
                  className="btn-primary"
                  style={{ width: '100%', marginTop: '1rem' }}
                  disabled={loading}
                  data-testid="place-order-btn"
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ background: '#1a1a1a', color: 'white', padding: '3rem 2rem', textAlign: 'center', marginTop: '4rem' }} data-testid="footer">
        <div className="container">
          <img src={LOGO_URL} alt="Om Jishnu" style={{ height: '60px', marginBottom: '1rem' }} />
          <p style={{ marginBottom: '0.5rem', fontSize: '1.2rem', fontWeight: '600' }}>OM JISHNU</p>
          <p style={{ color: '#999', marginBottom: '1.5rem' }}>Your trusted partner for printing solutions</p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>© 2025 Om Jishnu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CheckoutPage;